/**
 * Always-visible marker while this localhost page runs against a deployed
 * backend, so Prod data is never mistaken for local data. The × switches back
 * to the local backend.
 *
 * It also checks the relay is really there: a dev server started before the
 * relay existed answers relay paths with index.html, which would otherwise
 * surface as JSON parse errors all over the app.
 */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../components/ui';
import { DEV_BACKEND_RELAY_HEADER } from './backends';
import { activeRemoteBackend, switchDevBackend } from './client';
import './DevBackendBadge.css';

type RelayState = 'checking' | 'ok' | 'missing' | 'unreachable';

export default function DevBackendBadge() {
  const backend = activeRemoteBackend();
  const [relay, setRelay] = useState<RelayState>('checking');

  useEffect(() => {
    if (!backend) return undefined;
    let cancelled = false;
    // Relayed by the patched fetch, like every other /api call on this page.
    fetch('/api/auth/config', { cache: 'no-store' })
      .then((res) => {
        if (cancelled) return;
        if (res.headers.get(DEV_BACKEND_RELAY_HEADER) !== backend.id) setRelay('missing');
        else setRelay(res.status === 502 ? 'unreachable' : 'ok');
      })
      .catch(() => {
        if (!cancelled) setRelay('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [backend]);

  // Widget/headless iframes embedded in a local test page: the host page's own
  // badge (if any) is enough.
  if (!backend || window.self !== window.top) return null;

  const host = new URL(backend.origin).host;
  const detail = relay === 'missing'
    ? 'Relay not running: restart the frontend dev server'
    : relay === 'unreachable'
      ? `Can't reach ${host}`
      : host;

  return (
    <div className="dev-backend-badge" data-backend={backend.id} data-relay={relay} role="status">
      <div className="dev-backend-badge-pill">
        <span className="dev-backend-badge-label">{backend.label} backend</span>
        <span className="dev-backend-badge-detail">{detail}</span>
        <Button
          variant="ghost"
          size="icon-xs"
          className="dev-backend-badge-close size-5"
          onClick={() => switchDevBackend('local')}
          title="Switch back to the local backend"
          aria-label="Switch back to the local backend"
        >
          <X className="size-3" />
        </Button>
      </div>
    </div>
  );
}
